/**
 * Med-management approval note: the decided medication plan, then rationale.
 */

export const STANDARD_MEDICATIONS = [
  'APIXABAN', 'ELIQUIS', 'DABIGATRAN_ETEXILATE', 'DABIGATRAN', 'PRADAXA',
  'EDOXABAN', 'LIXIANA', 'SAVAYSA', 'RIVAROXABAN', 'CERELTO', 'WARFARIN',
  'COUMADIN', 'JANTOVIN', 'ASPIRIN', 'BAYER', 'CLOPIDOGREL', 'PLAVIX',
  'PRASUGREL', 'EFFIENT', 'TICAGRELOR', 'BRILINTA', 'ANTIPLATELET', 'XARELTO',
];

export const NO_MEDICATIONS_DETECTED_MESSAGE = 'No antithrombotic medication detected';

const THROMBO_DIMENSION_RATIONALE_LABEL = {
  ATRIAL_FIBRILLATION: 'atrial fibrillation',
  THROMBOSIS: 'thrombosis / thromboembolic burden',
  PROSTHETIC_HEART_VALVE: 'prosthetic heart valve',
};

const THROMBO_DIMENSION_OMIT_FROM_RATIONALE = new Set(['THROMBOSIS']);

const THROMBO_RISK_DIMENSIONS = [
  ['ATRIAL_FIBRILLATION', 'Atrial fibrillation'],
  ['THROMBOSIS', 'Thrombosis / thromboembolic burden'],
  ['PROSTHETIC_HEART_VALVE', 'Prosthetic heart valve'],
];

const THROMBO_RELATED_CONDITIONS = new Set([
  'ATRIAL_FIBRILLATION',
  'DEEP_VEIN_THROMBOSIS',
  'VENOUS_THROMBOEMBOLISM',
  'RECURRENT_VENOUS_THROMBOEMBOLISM',
  'STROKE',
  'TRANSIENT_ISCHAEMIC_ATTACK',
  'LEFT_VENTRICULAR_THROMBUS',
  'RHEUMATIC_VALVE_HEART_DISEASE',
  'ANTI_PHOSPHOLIPID_SYNDROME',
]);

const CONDITION_RATIONALE_OVERRIDES = {
  ANTI_PHOSPHOLIPID_SYNDROME: 'anti-phospholipid syndrome',
};

const RATIONALE_INTRO =
  "This personalized recommendation, specific to the patient's current " +
  'antithrombotic medication, is based on their modifiable patient factors.';

const RATIONALE_CLOSING =
  'This pause/resumption schedule considers these risk factors in the context ' +
  'of the antithrombotic medication and the planned surgical procedure.';

export function medicationDosagePresent(details) {
  const d = details?.dosage;
  return d != null && String(d).trim() !== '';
}

export function filterApplicableMedications(mergedMedications) {
  return Object.entries(mergedMedications || {}).filter(([med]) =>
    STANDARD_MEDICATIONS.includes(med.toUpperCase())
  );
}

export function formatClinicalLabel(code) {
  if (code == null || code === '') return '';
  const s = String(code);
  return s
    .split('_')
    .map((word) =>
      word.length === 0 ? '' : `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`
    )
    .join(' ');
}

export function formatRationaleConditionPhrase(code) {
  if (!code) return '';
  if (CONDITION_RATIONALE_OVERRIDES[code]) return CONDITION_RATIONALE_OVERRIDES[code];
  const raw = formatClinicalLabel(code).trim();
  if (!raw) return '';
  return raw.charAt(0).toLowerCase() + raw.slice(1);
}

export function formatOxfordList(items) {
  const a = [
    ...new Set(
      items
        .map((x) => String(x).trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  if (a.length === 0) return '';
  if (a.length === 1) return a[0];
  if (a.length === 2) return `${a[0]} and ${a[1]}`;
  return `${a.slice(0, -1).join(', ')}, and ${a[a.length - 1]}`;
}

export function formatMedicationAction(med, details) {
  if (details?.bridge) return String(details.bridge);
  if (details?.pauseDays && details?.resumeDays) {
    return `Pause ${med} ${details.pauseDays} days before surgery, and resume ${details.resumeDays} days after surgery`;
  }
  if (details?.pauseDays) {
    return `Pause ${med} ${details.pauseDays} days before surgery`;
  }
  return 'No alterations needed';
}

export function formatMedicationPlanText(applicableMedications) {
  if (!applicableMedications || applicableMedications.length === 0) {
    return NO_MEDICATIONS_DETECTED_MESSAGE;
  }
  return applicableMedications
    .map(([med, details]) => {
      const dosageLine = medicationDosagePresent(details)
        ? `${med}: Dosage: ${details.dosage}`
        : `${med}: Dosage: Dosage Missing from EHR`;
      return `${dosageLine}\n${formatMedicationAction(med, details)}`;
    })
    .join('\n\n');
}

export function collectThromboAndBleedParts({
  risks,
  conditions,
  medicationKeys = [],
} = {}) {
  const risksObj = risks || null;
  const conditionsArr = Array.isArray(conditions) ? conditions : [];
  const thromboParts = [];
  const thromboSeen = new Set();
  const addThromboPhrase = (phrase) => {
    const key = String(phrase || '').trim().toLowerCase();
    if (!key || thromboSeen.has(key)) return;
    thromboSeen.add(key);
    thromboParts.push(phrase);
  };

  if (risksObj) {
    THROMBO_RISK_DIMENSIONS.forEach(([k]) => {
      if (THROMBO_DIMENSION_OMIT_FROM_RATIONALE.has(k)) return;
      const v = risksObj[k];
      if (v == null || v < 1) return;
      const label = THROMBO_DIMENSION_RATIONALE_LABEL[k];
      if (label) addThromboPhrase(label);
    });
    conditionsArr.forEach((c) => {
      const code = typeof c === 'string' ? c : '';
      if (!code || !THROMBO_RELATED_CONDITIONS.has(code)) return;
      if (
        code === 'ATRIAL_FIBRILLATION' &&
        (risksObj.ATRIAL_FIBRILLATION ?? 0) >= 1
      ) {
        return;
      }
      addThromboPhrase(formatRationaleConditionPhrase(code));
    });
  }

  const bleedParts = [];
  if (conditionsArr.includes('SUPRATHERAPEUTIC_INR')) {
    bleedParts.push('supratherapeutic inr');
  }
  if (conditionsArr.includes('ALCOHOL_USE')) {
    bleedParts.push('alcohol use');
  }
  const medicationKeysUpper = medicationKeys.map((m) => String(m).toUpperCase());
  if (medicationKeysUpper.some((m) => m === 'ASPIRIN')) {
    bleedParts.push('aspirin');
  }
  if (medicationKeysUpper.some((m) => m.includes('ANTIPLATELET'))) {
    bleedParts.push('antiplatelet');
  }

  return { thromboParts, bleedParts };
}

export function formatRationaleText({ thromboParts = [], bleedParts = [] } = {}) {
  const hasThrombo = thromboParts.length > 0;
  const hasBleed = bleedParts.length > 0;
  let riskSentence = '';
  if (hasThrombo || hasBleed) {
    const chunks = [];
    if (hasThrombo) {
      chunks.push(`Their risk of thrombosis is elevated due to ${formatOxfordList(thromboParts)}`);
    }
    if (hasBleed) {
      const bleedClause = `bleeding risk is elevated due to ${formatOxfordList(bleedParts)}`;
      chunks.push(hasThrombo ? `their ${bleedClause}` : `Their ${bleedClause}`);
    }
    riskSentence = ` ${chunks.join(', while ')}.`;
  }
  return `${RATIONALE_INTRO}${riskSentence} ${RATIONALE_CLOSING}`;
}

export function buildMedManagementApprovalNote({
  parsedOutput,
  mergedMedications,
  status,
} = {}) {
  const merged = mergedMedications || {};
  const medicationsPresent = Boolean(parsedOutput?.medications);
  const applicable = medicationsPresent
    ? filterApplicableMedications(merged)
    : null;
  const planText = applicable == null ? '' : formatMedicationPlanText(applicable);
  const { thromboParts, bleedParts } = collectThromboAndBleedParts({
    risks: parsedOutput?.risks,
    conditions: parsedOutput?.conditions,
    medicationKeys: Object.keys(merged),
  });
  const rationaleText = formatRationaleText({ thromboParts, bleedParts });
  const sections = [];
  if (planText) {
    sections.push(`Medication plan:\n${planText}`);
  }
  sections.push(`Rationale:\n${rationaleText}`);
  const body = sections.join('\n\n');
  if (status === 'denied') {
    return `The following plan has been denied.\n\n${body}`;
  }
  return body;
}
